import type { WikiMeDto } from './authApi';
import type { CourseListItemDto, MyPathDto } from './wikiApi';
import type { HubSummaryDto } from './api/hub';
import type { Audience } from './nav';
import { levelOf as learnLevelOf } from '../components/learn/learn-utils';

/**
 * Чистые помощники главной HUB 2.0 (W1): приветствие, подстрока по роли,
 * уровень ученика, выбор курса для «Продолжить», день траектории. Отдельно от
 * HomePage.tsx — чтобы проверять vitest'ом без DOM.
 */

export const WEEKDAY_DATE = (d: Date): string => {
  const s = d.toLocaleDateString('ru-RU', { weekday: 'long', day: 'numeric', month: 'long' });
  return s.charAt(0).toUpperCase() + s.slice(1);
};

/** «Доброе утро / Добрый день / Добрый вечер» по часам браузера. */
export function greetingOf(hour: number): string {
  if (hour >= 5 && hour < 12) return 'Доброе утро';
  if (hour >= 12 && hour < 18) return 'Добрый день';
  if (hour >= 18 && hour < 23) return 'Добрый вечер';
  return 'Доброй ночи';
}

/** «1 точка / 2 точки / 5 точек». */
export function plural(n: number, forms: [string, string, string]): string {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return forms[0];
  if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return forms[1];
  return forms[2];
}
const pl = (n: number, forms: [string, string, string]): string => `${n.toLocaleString('ru-RU')} ${plural(n, forms)}`;

/** Имя для приветствия: у CRM «Фамилия Имя Отчество» → Имя, иначе первое слово. */
export function firstNameOf(me: WikiMeDto | undefined): string {
  const parts = (me?.fullName ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'коллега';
  // CRM и iiko отдают «Фамилия Имя [Отчество]».
  return parts.length >= 2 ? parts[1] : parts[0];
}

/** Подстрока приветствия по роли и скоупу (числа — только из summary). */
export function summaryLine(audience: Audience, s: HubSummaryDto | undefined, extra?: string | null): string {
  const parts: string[] = [];
  if (s) {
    if (s.scope === 'network' && audience === 'uk') {
      parts.push(`в сети ${pl(s.partners ?? 0, ['партнёр', 'партнёра', 'партнёров'])}, ${pl(s.points ?? 0, ['точка', 'точки', 'точек'])}, ${pl(s.learners ?? 0, ['ученик', 'ученика', 'учеников'])}`);
    } else if (s.scope === 'points') {
      if (s.pointName && s.points === 1) parts.push(s.pointName);
      else parts.push(pl(s.points ?? 0, ['точка', 'точки', 'точек']));
      if (s.staff) parts.push(pl(s.staff, ['сотрудник', 'сотрудника', 'сотрудников']));
    } else if (s.scope === 'point') {
      if (s.pointName) parts.push(s.pointName);
      if (audience === 'manager' && s.staff) parts.push(`команда ${pl(s.staff, ['человек', 'человека', 'человек'])}`);
    }
  }
  if (extra) parts.push(extra);
  return parts.join(' · ');
}

/** Уровень ученика по очкам — единая шкала обучения (components/learn/learn-utils.ts, W3). */
export function levelOf(points: number): string {
  return learnLevelOf(points).name;
}

/** Курс для «Продолжить»: начатый (назначенный и с ближайшим сроком — первым), иначе назначенный. */
export function pickContinueCourse(courses: CourseListItemDto[]): CourseListItemDto | null {
  const open = courses.filter((c) => !c.isCompleted && !c.isArchived);
  const byDue = (a: CourseListItemDto, b: CourseListItemDto): number =>
    Number(b.isAssigned) - Number(a.isAssigned) || (a.dueAt ?? '9999').localeCompare(b.dueAt ?? '9999');
  const started = open.filter((c) => c.progressPercent > 0).sort(byDue);
  if (started[0]) return started[0];
  const assigned = open.filter((c) => c.isAssigned || c.isRequired).sort(byDue);
  return assigned[0] ?? null;
}

/** «день 9 из 14 в траектории «Новый повар»». */
export function pathDayLine(p: MyPathDto, now: Date = new Date()): string {
  const start = new Date(p.assignedAt).getTime();
  const day = Math.max(1, Math.floor((now.getTime() - start) / 86_400_000) + 1);
  if (p.dueAt) {
    const total = Math.max(1, Math.round((new Date(p.dueAt).getTime() - start) / 86_400_000));
    return `день ${Math.min(day, total)} из ${total} в траектории «${p.name}»`;
  }
  return `траектория «${p.name}»: ${p.doneSteps} из ${p.totalSteps} шагов`;
}
